import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { faqs } from '@/data/products';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function FAQ() {
  return (
    <div data-testid="faq-page" className="px-6 lg:px-8 pt-[80px] pb-[100px] md:pt-[120px] md:pb-[120px]">
      <div className="max-w-3xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">FAQ</p>
          <h1 className="font-inter font-bold text-[38px] md:text-[54px] lg:text-[68px] leading-[1.1] tracking-tight text-km-white">
            Common questions.
          </h1>
          <p className="mt-6 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3]">
            Everything you need to know about our AI systems, toolkits, and how we work.
          </p>
        </RevealSection>

        <RevealSection delay={100}>
          <div className="mt-12">
            <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-[16px]">
              {faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="rounded-[10px] bg-km-card-gradient border border-km-gray-3 px-[30px] transition-colors duration-300 hover:border-km-gray-4"
                >
                  <AccordionTrigger className="font-inter font-medium text-base md:text-lg text-km-white py-6 hover:no-underline hover:text-km-gold transition-colors duration-300 [&>svg]:text-km-gold [&>svg]:h-5 [&>svg]:w-5 [&>svg]:duration-300 cursor-pointer">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-inter font-medium text-base text-km-gray-5 leading-[1.3] pb-6">
                    {faq.answer}
                    {faq.link && (
                      <a
                        href={faq.link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 font-inter font-medium text-sm text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer"
                      >
                        {faq.link.label} <ArrowRight className="w-4 h-4" />
                      </a>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </RevealSection>

        <RevealSection delay={200}>
          <div className="mt-20 text-center border-t border-km-gray-3 pt-16">
            <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">
              Still have questions?
            </h2>
            <p className="mt-3 font-inter font-medium text-base text-km-gray-5 leading-[1.3]">
              We're happy to help. Reach out and we'll get back to you within 24 hours.
            </p>
            <Link
              to="/contact"
              data-testid="faq-contact-cta"
              className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 rounded-[10px] border-2 border-km-gold/30 text-km-white font-inter font-medium text-sm tracking-wide hover:border-km-gold/60 hover:text-km-gold transition-colors duration-300 cursor-pointer"
            >
              Get in Touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </RevealSection>
      </div>
    </div>
  );
}
